import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { getMatterJurisdictionAnalyticsProps, resolveMatterJurisdictionEntrySource } from '@/analytics/utils/matter-jurisdiction-analytics'
import { CreateOrUpdateMatterForm, CreateOrUpdateMatterSchema, MatterRead } from '@/matter/store/schemas'
import { MatterDialogType, MattersActions } from '@/matter/store/slice'
import { RootState } from '@/store/store'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { getBrandName } from '@/util/enterprise'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { selectMatterById } from '../../store/selectors'
import FloatLabelFormInput from './FloatLabelFormInput'
import FloatLabelFormTextarea from './FloatLabelFormTextArea'
import {
  kBEMatterAlreadyExistsDetail,
  kBEMatterNameReservedDetail,
  kMatterDialogGenericErrorMessage,
  kMatterNameExistsErrorMessage,
  kMatterNameReservedErrorMessage,
  kMaxMatterContextLength,
  kMaxMatterHardLimit,
} from '@/matter/constants'
import MatterCharacterCounter from './MatterCharacterCounter'
import { ApiError } from '@/util/errors/api-error'
import { putUpdateMatter } from '@/matter/fetch/put-update-matter'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { postCreateMatter } from '@/matter/fetch/post-create-matter'
import { setMobileSidebarOpen } from '@/store/slices/ui-state.slice'
import { matterPaths } from '@/routing/paths'
import trim from 'lodash/trim'
import { useLocation, useMatch, useNavigate } from 'react-router-dom'
import { replaceMatterSegment } from '@/matter/utils/path'
import JurisdictionField from '@/jurisdiction-mappings/components/JurisdictionField'
import { JurisdictionKind, useJurisdictionFieldState } from '@/jurisdiction-mappings/jurisdiction-field-state'
import { useSettings } from '@/settings/hooks/useSettings'
import { selectDefaultJurisdiction } from '@/chat-common/store/chat-ux.selectors'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'

/**
 * Create or Edit Matter Dialog
 *
 * Dialog to create or edit a matter's details.
 */
export default function CreateOrEditMatterDialog() {
  const navigate = useNavigate()
  const { trackEvent } = useAnalytics()
  const dispatch = useAppDispatch()
  const brandName = getBrandName()
  const location = useLocation()
  const isUserFilesRoute = useMatch('/dashboard/user-files/*')
  const formRef = useRef<HTMLFormElement>(null)

  // Local state
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Redux state
  const dialogState = useAppSelector((state: RootState) => state.mattersState.dialog)
  const matter = useAppSelector((state: RootState) => selectMatterById(state, { matterId: dialogState.matterId }))
  const defaultJurisdiction = useAppSelector((state: RootState) => selectDefaultJurisdiction(state))
  const isMobileSidebarOpen = useAppSelector((state: RootState) => state.uiState.isMobileSidebarOpen)
  const { getSettings } = useSettings()

  const dialogType = dialogState.dialogType
  const isEditType = dialogType === MatterDialogType.Edit
  const isDialogOpen = dialogType === MatterDialogType.Create || dialogType === MatterDialogType.Edit

  // Initial form values based on whether we're editing a matter or creating a new one, only recalculated when the dialogType or matter changes
  const initialFormValues = useMemo<CreateOrUpdateMatterForm>(() => {
    return isEditType
      ? // Edit uses the existing matter's data
        {
          name: matter?.name ?? '',
          client: matter?.client ?? '',
          description: matter?.description ?? '',
        }
      : // Create uses empty values
        {
          name: '',
          client: '',
          description: '',
        }
  }, [isEditType, matter])

  const initialJurisdiction = isEditType ? matter?.default_jurisdiction ?? null : defaultJurisdiction
  const jurisdictionField = useJurisdictionFieldState(initialJurisdiction, isEditType ? JurisdictionKind.UNDECIDED : JurisdictionKind.STATE)

  const {
    register,
    handleSubmit,
    formState: { errors: formErrors, isValid, isDirty },
    reset,
    setFocus,
    setError: setFieldError,
    watch,
  } = useForm<CreateOrUpdateMatterForm>({
    resolver: zodResolver(CreateOrUpdateMatterSchema),
    mode: 'onChange',
    defaultValues: initialFormValues,
  })

  // Watch form values for character limit calculation
  const currentValues = watch()

  // Calculate total character count (raw user text only, excluding labels)
  const totalCharCount = useMemo(() => {
    const name = currentValues.name || ''
    const client = currentValues.client || ''
    const description = currentValues.description || ''

    return name.length + client.length + description.length
  }, [currentValues.name, currentValues.client, currentValues.description])

  // Allow submission if the form is valid, there are actual changes, and under hard limit
  const canSubmit = isValid && (isDirty || jurisdictionField.isDirty) && jurisdictionField.isValid && totalCharCount <= kMaxMatterHardLimit

  // Unique key per dialog session
  const formKey = `${dialogType}-${dialogState.matterId ?? 'new'}`

  /*
   * Reset the form when switching between create/edit modes or editing a different matter.
   * Tracked via `formKey`, which changes when dialog type or matter ID changes.
   */
  useEffect(() => {
    reset(initialFormValues)
    jurisdictionField.reset()
    setSubmitError(null)
    setIsSubmitting(false)

    // Autofocus the name field on Create. The shared Modal always focuses its own container on
    // open (see ModalContainer's onOpenAutoFocus), so we set focus ourselves after a short delay
    // to account for Radix dialog portal mounting and animations.
    if (isDialogOpen && !isEditType) {
      const timeoutId = setTimeout(() => setFocus('name'), 100)
      return () => clearTimeout(timeoutId)
    }
    // initialFormValues is derived from the same dependencies as formKey, so it's safe to exclude
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey, reset])

  useEffect(() => {
    if (!isEditType && dialogType === MatterDialogType.Create) {
      getSettings()
    }
    // getSettings is omitted so this runs once per create-dialog open, not once per settings hook render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogType, isEditType])

  // Handles success actions after a matter is created or updated
  const onSuccess = (savedMatter: MatterRead) => {
    // Update the store with the saved matter
    dispatch(MattersActions.upsertMatter(savedMatter))

    // If it's a create, set the new matter as the active matter (unless the caller opted out)
    if (!isEditType && dialogState.switchToMatterOnCreate) {
      // IMPORTANT: Navigate FIRST, then update Redux state.
      // This ensures the URL is correct when route handler effects run,
      // preventing race conditions and duplicate history entries.
      if (isUserFilesRoute) {
        const path = replaceMatterSegment(location.pathname, savedMatter.id)
        if (path !== location.pathname) {
          navigate(path)
        }
      }

      // Then update Redux state
      dispatch(MattersActions.setActiveMatterId({ matterId: savedMatter.id }))
      if (isMobileSidebarOpen) dispatch(setMobileSidebarOpen(false)) // When creating a new matter from the sidebar on small screens
    }

    if (!isEditType && dialogState.navigateToMatterOnCreate) {
      navigate(matterPaths.overview(savedMatter.id))
    }

    // Show a success toast
    const toastMsg = isEditType
      ? `Updated matter: ${savedMatter.name}`
      : dialogState.switchToMatterOnCreate
      ? `Created and switched to matter: ${savedMatter.name}`
      : `Created matter: ${savedMatter.name}`
    dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: toastMsg, durationMs: kToastDurationMediumMs, truncateAtLine: 3 }))

    setIsSubmitting(false)
    dispatch(MattersActions.closeDialog())
  }

  /**
   * Handles form submission for creating or editing a matter.
   *
   * - Sends form data to the appropriate API endpoint
   * - On success: tracks analytics, updates Redux state, shows a toast, and closes the dialog
   * - On error: sets appropriate field or global error messages for user feedback
   */
  const onSubmit = async (formData: CreateOrUpdateMatterForm) => {
    setSubmitError(null)

    const jurisdictionValidationError = jurisdictionField.validate()
    if (jurisdictionValidationError) {
      return
    }

    const matterPayload: CreateOrUpdateMatterForm = {
      ...formData,
      default_jurisdiction: jurisdictionField.value,
    }

    setIsSubmitting(true)

    let savedMatter: MatterRead

    try {
      if (isEditType) {
        // Edit Matter Form Submission
        if (!dialogState.matterId) {
          throw new Error('Editing a matter requires a matter id, but none was provided.')
        }
        savedMatter = await putUpdateMatter(dialogState.matterId, matterPayload)

        const savedJurisdiction = savedMatter.default_jurisdiction ?? null

        trackEvent(AnalyticsEvent.MatterUpdated, {
          matterNameChanged: (savedMatter.name ?? '') !== initialFormValues.name,
          matterClientChanged: (savedMatter.client ?? '') !== initialFormValues.client,
          matterDescriptionChanged: (savedMatter.description ?? '') !== initialFormValues.description,
          matterJurisdictionEntrySource: resolveMatterJurisdictionEntrySource(dialogState.source),
          matterJurisdictionChanged: savedJurisdiction !== initialJurisdiction,
          matterJurisdictionAdopted: initialJurisdiction == null && savedJurisdiction != null,
          ...getMatterJurisdictionAnalyticsProps(savedJurisdiction),
        })
      } else {
        // Create Matter Form Submission
        savedMatter = await postCreateMatter(matterPayload)

        trackEvent(AnalyticsEvent.MatterCreated, getMatterJurisdictionAnalyticsProps(savedMatter.default_jurisdiction))
      }

      // Perform success actions after a brief delay for a smoother visual transition
      setTimeout(() => onSuccess(savedMatter), 600)
    } catch (e) {
      // If there's a 409 Conflict (e.g. duplicate name or reserved name), display the error on the "name" field
      if (e instanceof ApiError && e.status === 409) {
        let errorMessage = kMatterDialogGenericErrorMessage
        if (e.detail === kBEMatterAlreadyExistsDetail) errorMessage = kMatterNameExistsErrorMessage
        else if (e.detail === kBEMatterNameReservedDetail) errorMessage = kMatterNameReservedErrorMessage

        setFieldError('name', {
          type: 'server',
          message: errorMessage,
        })
        setFocus('name')
      } else {
        setSubmitError(kMatterDialogGenericErrorMessage)
      }
      setIsSubmitting(false)
    }
  }

  return (
    <Modal open={isDialogOpen} onOpenChange={() => dispatch(MattersActions.closeDialog())}>
      <ModalContent
        key={formKey}
        title="Matter Details"
        subtitle={`Add case details to help ${brandName} understand and assist with this matter.`}
        width="md"
        height="max-full"
        preventDismiss
        footer={
          <ModalFooterWithActions
            tertiaryText={
              <span>
                <span className="text-red-700">*</span> Required fields
              </span>
            }
            secondaryAction={{ label: 'Cancel', onClick: () => dispatch(MattersActions.closeDialog()) }}
            primaryAction={{
              label: 'Save',
              onClick: () => formRef.current?.requestSubmit(),
              loading: isSubmitting,
              disabled: !canSubmit,
            }}
            inlineMessage={submitError ? { variant: 'error', children: submitError } : undefined}
          />
        }
      >
        <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {/* Matter Name */}
            <FloatLabelFormInput
              id="matter-name"
              label="Matter Name"
              required
              error={formErrors.name?.message}
              registration={register('name', {
                setValueAs: trim,
              })}
            />

            <JurisdictionField
              {...jurisdictionField.fieldState}
              idPrefix="matter-jurisdiction"
              error={jurisdictionField.error ?? undefined}
              onChange={jurisdictionField.onChange}
            />

            {/* Client */}
            <FloatLabelFormInput
              id="matter-client"
              label="Client"
              error={formErrors.client?.message}
              registration={register('client', {
                setValueAs: trim,
              })}
            />

            {/* Description */}
            <FloatLabelFormTextarea
              id="matter-description"
              label="Matter Context"
              error={formErrors.description?.message}
              registration={register('description', {
                setValueAs: trim,
              })}
            />
          </div>

          {/* Character counter */}
          <MatterCharacterCounter totalCharCount={totalCharCount} softLimit={kMaxMatterContextLength} hardLimit={kMaxMatterHardLimit} />
        </form>
      </ModalContent>
    </Modal>
  )
}
